import { useEffect, useState, createContext, useContext } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { RefreshCw, Sparkles } from 'lucide-react';

const UpdateContext = createContext({ hasUpdate: false });

export const useUpdateStatus = () => useContext(UpdateContext);

export function UpdatePromptProvider({ children }: { children: React.ReactNode }) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {

      if (r) {
        setInterval(() => {
          r.update();
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {

    },
  });

  useEffect(() => {
    if (needRefresh) {
      setShowPrompt(true);
      document.body.style.overflow = 'hidden';
    }
  }, [needRefresh]);

  const handleUpdate = () => {
    setIsUpdating(true);
    updateServiceWorker(true);
  };

  return (
    <UpdateContext.Provider value={{ hasUpdate: showPrompt }}>
      {children}
      {showPrompt && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 animate-in fade-in duration-300" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 backdrop-blur-xl" style={{ backdropFilter: 'blur(20px)' }} />

          <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-200 p-8 max-w-md w-full mx-4 animate-in zoom-in-90 duration-500">
            <div className="flex flex-col items-center text-center">
              <div className="relative mb-6">
                <div className="absolute inset-0 bg-blue-400 rounded-full blur-2xl opacity-50 animate-pulse" />
                <div className="relative w-24 h-24 bg-gradient-to-br from-blue-500 via-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-2xl">
                  <RefreshCw className="w-12 h-12 text-white animate-spin" style={{ animationDuration: '3s' }} />
                </div>
              </div>

              <div className="mb-3 flex items-center gap-2 justify-center">
                <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
                <h2 className="text-3xl font-bold text-gray-900">
                  גרסה חדשה זמינה!
                </h2>
                <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
              </div>

              <div className="w-16 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent mb-6 rounded-full" />

              <p className="text-lg text-gray-700 mb-2 leading-relaxed font-medium">
                יש לנו עדכונים חדשים ושיפורים חשובים
              </p>
              <p className="text-base text-gray-500 mb-8">
                כדי להמשיך להשתמש במערכת, יש לרענן את האפליקציה
              </p>

              <button
                onClick={handleUpdate}
                disabled={isUpdating}
                className="w-full bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800 text-white px-8 py-5 rounded-2xl font-bold text-xl hover:from-blue-700 hover:via-blue-800 hover:to-blue-900 transition-all duration-300 flex items-center justify-center gap-3 shadow-2xl hover:shadow-blue-500/50 disabled:opacity-70 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                {isUpdating ? (
                  <>
                    <div className="w-7 h-7 border-4 border-white border-t-transparent rounded-full animate-spin" />
                    <span>מעדכן את המערכת...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-7 h-7" />
                    <span>רענן עכשיו</span>
                  </>
                )}
              </button>

              <div className="mt-8 flex items-center gap-2 text-sm text-gray-400">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <p>העדכון ייקח רק כמה שניות</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </UpdateContext.Provider>
  );
}

export function UpdatePrompt() {
  return null;
}
